import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal } from "risa-oasis-ui_v2";

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useSelector } from "react-redux";
import {
  DEFAULT_HEALTH_FACILITY_FEATURE_CONFIG,
  HEALTH_FACILITY_FEATURE_CONFIG_KEYS,
  type HealthCareFacilityModel,
  type HealthFacilityFeatureConfigKey,
} from "../../../data-model/healthFacility";
import { useAuthStatusFilter } from "../../../hooks/useAuthStatusFilter";
import { useFacilityFormData } from "../../../hooks/useFacilityFormData";
import { useHealthCareFacilities } from "../../../hooks/useHealthCareFacilities";
import { fetchClientFromFirebase } from "../../../redux/slice/clientConfiguration";
import { fetchHealthFacilityFromFirebase } from "../../../redux/slice/healthFacilitySlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { ToastMessages } from "../../toasts/toastMessages";
import AddressDetailsSection from "./components/AddressDetailsSection";
import PrimaryDetailsSection from "./components/PrimaryDetailsSection";
import {
  addFacilityToDb as addFacilityToDbUtil,
  updateFacilityInDb as updateFacilityInDbUtil,
} from "./facilityDbOperations";
import { initialFacilityData } from "./facilityModalConstants";

interface NewFacilityModalProps {
  data: HealthCareFacilityModel | null;
  index?: number;
  onModalClose?: (boolean) => void;
  type: "add" | "edit";
}

/**
 * Renders the facility onboarding and editing modal.
 *
 * @param props - Modal mode, callbacks, and optional existing facility data.
 * @returns The facility form modal.
 */
const NewFacilityModal = (props: NewFacilityModalProps) => {
  const { data: clientName } = useSelector(
    (state: RootState) => state.clientConfiguration,
  );

  const [resetFormData, setResetFormData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serviceTypesInput, setServiceTypesInput] = useState("");

  const { facilityData, setFacilityData } = useFacilityFormData(
    props.data,
    initialFacilityData,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const dispatch = useDispatch<AppDispatch>();

  // Initialize auth status filter hook (only fetches when facilityId is provided in edit mode)
  const {
    selectedAuthStatusFilter,
    handleAuthStatusFilterChange,
    handleRemoveAuthStatusFilter,
    getAvailableAuthStatusOptions,
    authStatusFilterPlaceholder,
    hasNoOptionsAvailable,
  } = useAuthStatusFilter(
    props.type === "edit" ? props.data?.auth_status_filter : undefined,
    props.type === "edit" ? props.data?.DocId : undefined,
  );

  useEffect(() => {
    dispatch(fetchClientFromFirebase());
  }, [dispatch]);

  useEffect(() => {
    setServiceTypesInput((props.data?.service_types ?? []).join(", "));
  }, [props.data]);
  const emrOptions = useMemo(() => {
    return [
      {
        label: "Onco EMR",
        value: "oncoemr",
      },
      {
        label: "Mod Med",
        value: "modmed",
      },
    ];
  }, []);

  const categoryOptions = useMemo(() => {
    return [
      {
        label: "MedOnc",
        value: "med_onc",
      },
      {
        label: "Radiology",
        value: "radiology",
      },
      {
        label: "Urology",
        value: "urology",
      },
    ];
  }, []);

  const clientOptions = useMemo(() => {
    return clientName.map((client) => ({
      label: client.name ?? "",
      value: client.DocId ?? "",
    }));
  }, [clientName]);

  const { healthCareFacilities } = useHealthCareFacilities();

  // Filter to only internal organizations for the dropdown
  const internalFacilityOptions = useMemo(
    () =>
      healthCareFacilities.data
        ?.filter((f) => f.is_external_organization !== true)
        .map((f) => ({ value: f.DocId ?? "", label: f.Name ?? "" })) ?? [],
    [healthCareFacilities.data],
  );

  const areAllFieldsFilled = (): boolean => {
    const {
      Name,
      NPI,
      TaxID,
      PhoneNumber,
      Address,
      FhirId,
      emr_name,
      fhir_client,
      RisaPhoneNumber,
      RisaFaxNumber,
      client_id,
    } = facilityData;

    if (
      !Name ||
      !NPI ||
      !TaxID ||
      !PhoneNumber ||
      !FhirId ||
      !emr_name ||
      !fhir_client ||
      !RisaPhoneNumber ||
      !RisaFaxNumber ||
      !client_id
    ) {
      return false;
    }

    // if (is_external_organization && !organization_id) {
    //   return false;
    // }

    for (const field in Address) {
      if (!Address[field as keyof typeof Address]) {
        return false;
      }
    }

    return true;
  };

  const handleClientSelection = (selectedData) => {
    const { value } = selectedData;

    const selectedClient = clientName.find((client) => client.DocId === value);

    if (selectedClient) {
      setFacilityData((prev) => ({
        ...prev,
        client_id: value,
        NPI: selectedClient.NPI || "",
        TaxID: selectedClient.TaxID || "",
        PhoneNumber: selectedClient.PhoneNumber || "",
        FhirId: selectedClient.FhirId || "",
        Address: {
          StreetAddress: selectedClient.Address?.StreetAddress || "",
          City: selectedClient.Address?.City || "",
          State: selectedClient.Address?.State || "",
          ZIP: selectedClient.Address?.ZIP || "",
        },
        emr_name: selectedClient.emr_name || "",
        fhir_client: selectedClient.fhir_client || "",
        RisaFaxNumber: selectedClient.RisaFaxNumber || "",
        RisaPhoneNumber: selectedClient.RisaPhoneNumber || "",
        category: selectedClient.category || "",
      }));
    }
  };

  /**
   * Applies a primary facility-form field update to local state.
   * Converts `reasons_min_count` to a number and clears it when the input is empty.
   *
   * @param data - Changed input name and value.
   * @returns Nothing.
   */
  const handleInputChangeForPrimaryDetails = (data) => {
    const { name, value } = data;
    if (
      HEALTH_FACILITY_FEATURE_CONFIG_KEYS.includes(
        name as HealthFacilityFeatureConfigKey,
      )
    ) {
      setFacilityData((prev) => ({
        ...prev,
        feature_config: {
          ...DEFAULT_HEALTH_FACILITY_FEATURE_CONFIG,
          ...prev.feature_config,
          [name]: value as boolean,
        },
      }));
      return;
    }
    setFacilityData((prev) => ({
      ...prev,
      [name]:
        name === "reasons_min_count"
          ? value === ""
            ? undefined
            : Number(value)
          : value,
    }));
  };

  const handleServiceTypesChange = (data: { value: string }) => {
    setServiceTypesInput(data.value);
  };

  const handleChangeForAddressDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      Address: {
        ...prev.Address,
        [name]: value,
      },
    }));
  };

  const handleOnClose = () => {
    props.onModalClose && props.onModalClose(false);
  };

  const addFacilityToDb = async () => {
    try {
      setLoading(true);

      await addFacilityToDbUtil({
        facilityData,
        serviceTypesInput,
        userEmail: user?.email,
      });

      dispatch(fetchHealthFacilityFromFirebase());
      toast.success(ToastMessages.FACILITY_ADDED_SUCCESS);
      handleOnClose();
    } catch (error) {
      console.error("Error adding facility to firebase", error);
      toast.error(ToastMessages.FACILITY_ADDED_FAILURE);
    } finally {
      setLoading(false);
    }
  };

  const updateFacilityInDb = async () => {
    setLoading(true);
    try {
      await updateFacilityInDbUtil({
        facilityId: props.data?.DocId || "",
        existingData: props.data,
        facilityData,
        selectedAuthStatusFilter,
        serviceTypesInput,
        userEmail: user?.email,
      });
      dispatch(fetchHealthFacilityFromFirebase());
      toast.success(ToastMessages.FACILITY_UPDATED_SUCCESS);
      handleOnClose();
    } catch (error) {
      console.error("Error updating facility to firebase", error);
      toast.error(ToastMessages.FACILITY_UPDATED_FAILURE);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resetFormData) {
      setResetFormData(false);
    }
  }, [facilityData]);

  return (
    <Modal
      dialogId={"new-facility-modal"}
      onSave={props.type === "add" ? addFacilityToDb : updateFacilityInDb}
      title={props.type === "add" ? "Onboard New Facility" : "Edit Facility"}
      saveButtonText={loading ? "Saving..." : "Save"}
      cancelText={"Cancel"}
      heightPercentage={70}
      disableSave={!areAllFieldsFilled() || loading}
      onClose={handleOnClose}
    >
      <div className="facility-form-container">
        <form>
          <PrimaryDetailsSection
            facilityData={facilityData}
            resetFormData={resetFormData}
            handleClientSelection={handleClientSelection}
            handleInputChangeForPrimaryDetails={
              handleInputChangeForPrimaryDetails
            }
            clientOptions={clientOptions}
            emrOptions={emrOptions}
            type={props.type}
            selectedAuthStatusFilter={selectedAuthStatusFilter}
            handleAuthStatusFilterChange={handleAuthStatusFilterChange}
            handleRemoveAuthStatusFilter={handleRemoveAuthStatusFilter}
            getAvailableAuthStatusOptions={getAvailableAuthStatusOptions}
            authStatusFilterPlaceholder={authStatusFilterPlaceholder}
            hasNoOptionsAvailable={hasNoOptionsAvailable}
            facilityOptions={internalFacilityOptions}
            categoryOptions={categoryOptions}
            serviceTypesValue={serviceTypesInput}
            handleServiceTypesChange={handleServiceTypesChange}
          />
          <AddressDetailsSection
            facilityData={facilityData}
            resetFormData={resetFormData}
            handleChangeForAddressDetails={handleChangeForAddressDetails}
          />
        </form>
      </div>
    </Modal>
  );
};

export default NewFacilityModal;
